import React, { useEffect, useState } from "react";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Phone, CalendarBlank, CheckCircle, XCircle, ClockCounterClockwise, EnvelopeSimple } from "@phosphor-icons/react";

const STATUS = {
  pending:     { label: "Pending",     cls: "border-exodus-border text-exodus-muted" },
  called:      { label: "Called",      cls: "border-blue-500/40 text-blue-300" },
  completed:   { label: "Completed",   cls: "border-emerald-500/40 text-emerald-300" },
  no_answer:   { label: "No answer",   cls: "border-red-500/40 text-red-300" },
  rescheduled: { label: "Rescheduled", cls: "border-yellow-500/40 text-yellow-300" },
};
const NEXT_STATES = ["pending", "called", "completed", "no_answer", "rescheduled"];

const fmtDate = (iso) => iso ? new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "—";
const fmtTs = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function AdminCallbacks() {
  const [win, setWin] = useState("upcoming");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (w = win) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/callbacks?window=${w}`);
      setData(r.data);
    } catch {
      toast.error("Could not load callbacks. Sign in as advisor.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(win); /* eslint-disable-next-line */ }, [win]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/callbacks/${id}`, { status });
      toast.success(`Marked ${STATUS[status].label}`);
      setData((d) => ({ ...d, callbacks: d.callbacks.map((c) => c._id === id || c.id === id ? { ...c, status } : c) }));
    } catch {
      toast.error("Update failed");
    }
  };

  const today = data?.today || "";
  const tomorrow = data?.tomorrow || "";

  const callbacks = data?.callbacks || [];
  const grouped = callbacks.reduce((acc, c) => {
    (acc[c.preferred_date] = acc[c.preferred_date] || []).push(c);
    return acc;
  }, {});
  const sortedDates = Object.keys(grouped).sort();

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-10" data-testid="admin-callbacks-page">
        <div className="mb-8">
          <p className="eyebrow"><span className="gold-divider" />Admin · Callback Requests</p>
          <h1 className="font-display text-4xl mt-3">Your callback list.</h1>
          <p className="text-sm text-exodus-muted mt-2 max-w-2xl">
            Clients who requested a callback with the date and window they picked. Tap the phone number to call directly. Mark each one as you work through the list.
          </p>
        </div>

        {/* Window tabs */}
        <div className="grid grid-cols-4 border border-exodus-border mb-6" data-testid="callback-window-tabs">
          {[
            { v: "upcoming", label: "Upcoming" },
            { v: "today",    label: "Today" },
            { v: "tomorrow", label: "Tomorrow" },
            { v: "all",      label: "All" },
          ].map((t) => (
            <button
              key={t.v}
              data-testid={`callback-window-${t.v}`}
              onClick={() => setWin(t.v)}
              className={`px-4 py-3 text-sm transition-colors ${win === t.v ? "bg-exodus-gold text-exodus-bg" : "bg-exodus-surface hover:bg-exodus-elevated"}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {loading ? (
          <p className="text-exodus-muted text-sm">Loading callbacks…</p>
        ) : callbacks.length === 0 ? (
          <div className="card-surface p-10 text-center text-exodus-muted">
            No callback requests in this window.
          </div>
        ) : (
          <div className="space-y-8">
            {sortedDates.map((d) => (
              <div key={d}>
                <div className="flex items-center gap-3 mb-3">
                  <CalendarBlank size={16} className="text-exodus-gold" />
                  <p className="font-display text-xl">{fmtDate(d)}</p>
                  {d === today && <span className="text-[10px] eyebrow gold-text border border-exodus-gold px-2 py-0.5">Today</span>}
                  {d === tomorrow && <span className="text-[10px] eyebrow text-blue-300 border border-blue-500/40 px-2 py-0.5">Tomorrow</span>}
                  <span className="text-xs text-exodus-muted">{grouped[d].length} request{grouped[d].length === 1 ? "" : "s"}</span>
                </div>
                <div className="space-y-3">
                  {grouped[d].map((c) => (
                    <CallbackCard key={c._id || c.id || c.phone} c={c} onStatus={(s) => updateStatus(c._id || c.id, s)} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}

function CallbackCard({ c, onStatus }) {
  const stStyle = STATUS[c.status] || STATUS.pending;
  const telNum = "+1" + (c.phone || "").replace(/\D/g, "");
  return (
    <div data-testid={`callback-card-${c._id || c.id}`} className="card-surface p-5">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="font-display text-lg">{c.full_name}</p>
          <p className="text-xs text-exodus-muted mt-1">{c.preferred_time_slot} window</p>
          <div className="mt-3 flex flex-wrap gap-4 text-sm">
            <a href={`tel:${telNum}`} className="inline-flex items-center gap-2 gold-text hover:underline"
               data-testid={`callback-call-${c._id || c.id}`}>
              <Phone size={14} weight="fill" /> {c.phone}
            </a>
            <a href={`mailto:${c.email}`} className="inline-flex items-center gap-2 text-exodus-muted hover:text-exodus-text">
              <EnvelopeSimple size={14} /> {c.email}
            </a>
          </div>
          {c.topic && (
            <p className="text-xs text-exodus-muted mt-3 leading-relaxed border-l-2 border-exodus-gold-deep pl-3">
              {c.topic}
            </p>
          )}
          <p className="text-[10px] text-exodus-muted mt-3">Booked {fmtTs(c.created_at)}</p>
        </div>
        <span className={`text-[10px] eyebrow tracking-wider border px-2 py-1 ${stStyle.cls}`}>{stStyle.label}</span>
      </div>

      <div className="mt-4 pt-4 border-t border-exodus-border flex flex-wrap gap-2">
        {NEXT_STATES.map((s) => {
          const st = STATUS[s];
          const Icon = s === "completed" ? CheckCircle : s === "no_answer" ? XCircle : s === "rescheduled" ? ClockCounterClockwise : Phone;
          return (
            <button
              key={s}
              data-testid={`callback-status-${s}-${c._id || c.id}`}
              onClick={() => onStatus(s)}
              className={`text-xs inline-flex items-center gap-1.5 px-3 py-2 border transition-colors ${c.status === s ? st.cls + " ring-1 ring-exodus-gold" : "border-exodus-border text-exodus-muted hover:text-exodus-text hover:border-exodus-gold-deep"}`}
            >
              <Icon size={12} /> {st.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
